import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, TextField, Tooltip } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

export default function CountdownClock({ deadline, onUpdateDeadline }) {
  const [timeLeft, setTimeLeft] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(deadline || '');

  useEffect(() => {
    setEditValue(deadline || '');
  }, [deadline]);

  useEffect(() => {
    if (!deadline) return;
    const target = new Date(deadline.replace(' ', 'T'));
    if (isNaN(target.getTime())) return;

    function calculate() {
      const now = new Date();
      const diff = target - now;
      const absDiff = Math.abs(diff);
      const expired = diff < 0;

      const days = Math.floor(absDiff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((absDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((absDiff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((absDiff % (1000 * 60)) / 1000);
      setTimeLeft({ days, hours, minutes, seconds, expired });
    }

    calculate();
    const interval = setInterval(calculate, 1000);
    return () => clearInterval(interval);
  }, [deadline]);

  async function handleSave() {
    if (onUpdateDeadline) await onUpdateDeadline(editValue);
    setEditing(false);
  }

  if (!timeLeft) return null;

  const { expired, days, hours, minutes, seconds } = timeLeft;
  const urgentColor = expired ? '#555' :
    days < 3 ? '#d32f2f' :
    days < 7 ? '#f57f17' : '#2e7d32';
  const urgentBg = expired ? '#f5f5f5' :
    days < 3 ? '#ffebee' :
    days < 7 ? '#fff8e1' : '#e8f5e9';

  return (
    <Box sx={{ mt: 1, mb: 1 }}>
      <Box sx={{
        display: 'inline-flex', alignItems: 'center', gap: 1.5,
        bgcolor: urgentBg, borderRadius: 2, px: 2, py: 1,
        border: '1px solid', borderColor: urgentColor + '44'
      }}>
        <AccessTimeIcon sx={{ fontSize: 16, color: urgentColor }} />

        {/* Negativt tecken om expired */}
        {expired && (
          <Typography fontWeight={900} sx={{ color: urgentColor, fontSize: '1.4rem', lineHeight: 1 }}>
            −
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <TimeUnit value={days} label="dagar" color={urgentColor} />
          <Typography fontWeight="bold" sx={{ color: urgentColor, fontSize: '1.2rem' }}>:</Typography>
          <TimeUnit value={hours} label="tim" color={urgentColor} />
          <Typography fontWeight="bold" sx={{ color: urgentColor, fontSize: '1.2rem' }}>:</Typography>
          <TimeUnit value={minutes} label="min" color={urgentColor} />
          <Typography fontWeight="bold" sx={{ color: urgentColor, fontSize: '1.2rem' }}>:</Typography>
          <TimeUnit value={seconds} label="sek" color={urgentColor} />
        </Box>

        <Typography variant="caption" sx={{ color: urgentColor, fontWeight: 'bold' }}>
          {expired ? 'forsenad' : 'kvar'}
        </Typography>

        {/* Override-knapp */}
        {onUpdateDeadline && (
          <Tooltip title="Justera deadline">
            <IconButton size="small" onClick={() => setEditing(true)}
              sx={{ ml: 0.5, color: urgentColor, opacity: 0.7,
                '&:hover': { opacity: 1 } }}>
              <EditIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Override-formulär */}
      {editing && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <TextField
            size="small"
            value={editValue}
            onChange={e => setEditValue(e.target.value)}
            placeholder="YYYY-MM-DD HH:MM"
            label="Ny deadline"
            sx={{ width: 220 }}
            onKeyDown={e => {
              if (e.key === 'Enter') handleSave();
              if (e.key === 'Escape') setEditing(false);
            }}
          />
          <Tooltip title="Spara">
            <IconButton size="small" color="success" onClick={handleSave}>
              <CheckIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Avbryt">
            <IconButton size="small" color="error" onClick={() => setEditing(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography variant="caption" color="text.secondary">
            t.ex. 2026-05-15 23:59
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function TimeUnit({ value, label, color }) {
  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography sx={{
        fontWeight: 900, fontSize: '1.4rem', lineHeight: 1,
        color, fontFamily: '"Segoe UI", monospace',
        minWidth: 36, display: 'block', textAlign: 'center'
      }}>
        {String(value).padStart(2, '0')}
      </Typography>
      <Typography sx={{ fontSize: '0.55rem', color, opacity: 0.8, lineHeight: 1 }}>
        {label}
      </Typography>
    </Box>
  );
}